import { useState, useRef, useSyncExternalStore } from 'react'
import { useAutoRetry } from '../../hooks/useAutoRetry'
import ErrorRetryBar from '../../components/ErrorRetryBar'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Button, Input, Text, Badge, Spinner, Checkbox, Dialog, DialogSurface,
  DialogTitle, DialogBody, DialogActions, DialogContent, MessageBar,
  Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell,
  Menu, MenuTrigger, MenuPopover, MenuList, MenuItem,
  makeStyles, tokens,
} from '@fluentui/react-components'
import {
  Add24Regular, SearchRegular, Edit24Regular, Open24Regular,
  ArrowUpload24Regular, ChevronDownRegular,
} from '@fluentui/react-icons'
import {
  documentDefinitionsService,
  DocTypeLabels,
  type DocumentDefinitionListItem,
} from '../../services/documentDefinitionsService'
import { importsService, type LmsImportResult } from '../../services/importsService'
import { useAuth } from '../../auth/useAuth'

function useIsMobile(breakpoint = 640) {
  return useSyncExternalStore(
    cb => { window.addEventListener('resize', cb); return () => window.removeEventListener('resize', cb) },
    () => window.innerWidth <= breakpoint,
    () => false,
  )
}

const useStyles = makeStyles({
  header:  { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' },
  spacer:  { flex: 1 },
  toolbar: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' },
  row:     { cursor: 'pointer', '&:hover': { backgroundColor: tokens.colorNeutralBackground1Hover } },
  th: {
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    color: tokens.colorNeutralForeground3,
  },
})

const fmt = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString('cs-CZ') : '—'

export default function DocumentDefinitionsPage() {
  const styles = useStyles()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { isHR, isAdmin } = useAuth()
  const canEdit = isHR || isAdmin

  const [showInactive, setShowInactive] = useState(false)
  const [search, setSearch] = useState('')
  const [lmsImporting, setLmsImporting] = useState(false)
  const [lmsResult, setLmsResult] = useState<LmsImportResult | null>(null)
  const [lmsError, setLmsError] = useState<string | null>(null)
  const lmsFileRef = useRef<HTMLInputElement>(null)

  const handleLmsImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setLmsImporting(true)
    setLmsError(null)
    try {
      const result = await importsService.importLmsResults(file)
      setLmsResult(result)
    } catch (err: any) {
      setLmsError(err?.response?.data?.detail ?? err?.message ?? 'Chyba importu')
    } finally {
      setLmsImporting(false)
    }
  }

  const { data = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['document-definitions', showInactive],
    queryFn: () => documentDefinitionsService.getAll(showInactive),
  })

  const { autoRetryCount, autoRetryCountdown, maxRetries, resetAndRetry } = useAutoRetry(isError, refetch)

  const filtered = search.trim()
    ? data.filter((d: DocumentDefinitionListItem) =>
        d.name.toLowerCase().includes(search.toLowerCase()))
    : data

  if (isLoading) return <Spinner label="Načítání..." />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Skrytý file input pro LMS import */}
      {canEdit && (
        <input
          type="file"
          accept=".xlsx"
          ref={lmsFileRef}
          style={{ display: 'none' }}
          onChange={handleLmsImport}
        />
      )}

      {/* Dialog výsledku LMS importu */}
      <Dialog open={!!lmsResult || !!lmsError} onOpenChange={() => { setLmsResult(null); setLmsError(null) }}>
        <DialogSurface style={{ width: '720px', maxWidth: '95vw' }}>
          <DialogTitle>Import výsledků z LMS</DialogTitle>
          <DialogBody>
            <DialogContent>
              {lmsError && (
                <MessageBar intent="error" style={{ marginBottom: 12 }}>{lmsError}</MessageBar>
              )}
              {lmsResult && (
                <>
                  {/* Celkový souhrn */}
                  <div style={{ display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
                    {[
                      { label: 'Nově přiřazeno a splněno', value: lmsResult.created, color: tokens.colorPaletteGreenForeground1 },
                      { label: 'Aktualizováno', value: lmsResult.updated, color: tokens.colorBrandForeground1 },
                      { label: 'Přeskočeno (beze změny)', value: lmsResult.skipped, color: tokens.colorNeutralForeground3 },
                      { label: 'Nenalezený dokument', value: lmsResult.notFound, color: tokens.colorStatusDangerForeground1 },
                    ].map(item => (
                      <div key={item.label} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 28, fontWeight: 700, color: item.value > 0 ? item.color : tokens.colorNeutralForeground3 }}>{item.value}</div>
                        <div style={{ fontSize: 11, color: tokens.colorNeutralForeground3 }}>{item.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Tabulka po dokumentech */}
                  {lmsResult.documents.length > 0 && (
                    <>
                      <Text weight="semibold" size={300} style={{ display: 'block', marginBottom: 8 }}>Přehled po dokumentech</Text>
                      <div style={{ overflowX: 'auto', marginBottom: 16 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ borderBottom: `2px solid ${tokens.colorNeutralStroke1}` }}>
                              <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', color: tokens.colorNeutralForeground3 }}>Dokument</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', color: tokens.colorNeutralForeground3 }}>Párovací kód</th>
                              <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', color: tokens.colorPaletteGreenForeground1 }}>✓ Splněno</th>
                              <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', color: tokens.colorStatusDangerForeground1 }}>✗ Nesplněno</th>
                              <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', color: tokens.colorNeutralForeground3 }}>Nové</th>
                              <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', color: tokens.colorNeutralForeground3 }}>Změněno</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lmsResult.documents.map((doc, i) => (
                              <tr key={doc.courseCode} style={{ background: i % 2 === 1 ? tokens.colorNeutralBackground2 : 'transparent', borderBottom: `1px solid ${tokens.colorNeutralStroke2}` }}>
                                <td style={{ padding: '6px 8px', fontWeight: 500 }}>{doc.documentName || '—'}</td>
                                <td style={{ padding: '6px 8px', color: tokens.colorNeutralForeground3, fontFamily: 'monospace', fontSize: 12 }}>{doc.courseCode}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'center', color: doc.completedCount > 0 ? tokens.colorPaletteGreenForeground1 : tokens.colorNeutralForeground3, fontWeight: doc.completedCount > 0 ? 600 : 400 }}>{doc.completedCount}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'center', color: doc.notCompletedCount > 0 ? tokens.colorStatusDangerForeground1 : tokens.colorNeutralForeground3 }}>{doc.notCompletedCount}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'center', color: doc.createdCount > 0 ? tokens.colorPaletteGreenForeground1 : tokens.colorNeutralForeground3 }}>{doc.createdCount}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'center', color: doc.updatedCount > 0 ? tokens.colorBrandForeground1 : tokens.colorNeutralForeground3 }}>{doc.updatedCount}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {/* Varování / chyby po osobách */}
                  {lmsResult.rows.filter(r => r.messages.length > 0).length > 0 && (
                    <>
                      <Text weight="semibold" size={300} style={{ display: 'block', marginBottom: 8 }}>Upozornění</Text>
                      <div style={{ maxHeight: 200, overflowY: 'auto', fontSize: 13, border: `1px solid ${tokens.colorNeutralStroke1}`, borderRadius: 4, padding: '8px 12px' }}>
                        {lmsResult.rows.filter(r => r.messages.length > 0).map(r => (
                          <div key={r.rowNumber} style={{ marginBottom: 6 }}>
                            <strong>{r.personName}</strong>{r.documentName ? ` (${r.documentName})` : ''}
                            {r.messages.map((m, i) => (
                              <div key={i} style={{ color: m.type === 'error' ? tokens.colorStatusDangerForeground1 : tokens.colorStatusWarningForeground1, paddingLeft: 12 }}>
                                {m.type === 'error' ? '✗' : '⚠'} {m.text}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </DialogContent>
          </DialogBody>
          <DialogActions>
            <Button appearance="primary" onClick={() => { setLmsResult(null); setLmsError(null) }}>Zavřít</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>

      {/* Řádek 1 — titulek + akce */}
      <div className={styles.header}>
        <Text size={600} weight="semibold">Dokumenty</Text>
        <div className={styles.spacer} />
        {canEdit && (
          <>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button icon={<ChevronDownRegular />} iconPosition="after" disabled={lmsImporting}>
                  {lmsImporting ? <Spinner size="tiny" /> : null}
                  Export &amp; Import
                </Button>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem icon={<ArrowUpload24Regular />} onClick={() => lmsFileRef.current?.click()}>
                    Import výsledků z LMS
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
            <Button appearance="primary" icon={<Add24Regular />} onClick={() => navigate('/documents/new')}>
              Nový dokument
            </Button>
          </>
        )}
      </div>

      {/* Řádek 2 — filtry + Celkem */}
      <div className={styles.toolbar}>
        <Input
          style={{ minWidth: 280 }}
          placeholder="Hledat dokument..."
          contentBefore={<SearchRegular />}
          value={search}
          onChange={(_, d) => setSearch(d.value)}
        />
        <Checkbox
          label="Zobrazit neaktivní"
          checked={showInactive}
          onChange={(_, d) => setShowInactive(!!d.checked)}
        />
        <div className={styles.spacer} />
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          Celkem: {filtered.length}
        </Text>
      </div>

      <ErrorRetryBar
        isError={isError}
        message="Nepodařilo se načíst dokumenty."
        autoRetryCount={autoRetryCount}
        autoRetryCountdown={autoRetryCountdown}
        maxRetries={maxRetries}
        onRetry={resetAndRetry}
      />

      {/* Tabulka */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      {filtered.length === 0 ? (
        <Text style={{ color: tokens.colorNeutralForeground3 }}>Žádné dokumenty.</Text>
      ) : (
        <Table style={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHeader>
            <TableRow className="sticky-head">
              <TableHeaderCell className={styles.th} style={{ width: '28%' }}>Název</TableHeaderCell>
              <TableHeaderCell className={styles.th} style={{ width: '11%' }}>Typ</TableHeaderCell>
              {!isMobile && <TableHeaderCell className={styles.th} style={{ width: '16%' }}>Platnost</TableHeaderCell>}
              {!isMobile && <TableHeaderCell className={styles.th} style={{ width: '10%' }}>Termín</TableHeaderCell>}
              {!isMobile && <TableHeaderCell className={styles.th} style={{ width: '12%' }}>Přiřazení</TableHeaderCell>}
              <TableHeaderCell className={styles.th} style={{ width: '9%' }}>Stav</TableHeaderCell>
              {canEdit && !isMobile && <TableHeaderCell style={{ width: '80px' }} />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((item: DocumentDefinitionListItem) => {
              const completePct = item.totalAssigned > 0
                ? Math.round((item.totalCompleted / item.totalAssigned) * 100)
                : 0
              return (
                <TableRow
                  key={item.id}
                  className={styles.row}
                  onClick={() => navigate(`/documents/${item.id}`)}
                >
                  {/* Název + odkaz na dokument */}
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Text size={300} weight="semibold">{item.name}</Text>
                      {item.documentUrl && (
                        <a
                          href={item.documentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: tokens.colorBrandForeground1, lineHeight: 1 }}
                          onClick={e => e.stopPropagation()}
                        >
                          <Open24Regular style={{ fontSize: 14 }} />
                        </a>
                      )}
                    </div>
                  </TableCell>
                  {/* Typ */}
                  <TableCell>
                    <Badge appearance="outline" color={item.documentType === 1 ? 'warning' : item.documentType === 3 ? 'success' : 'informative'}>
                      {DocTypeLabels[item.documentType]}
                    </Badge>
                  </TableCell>
                  {/* Platnost */}
                  {!isMobile && (
                    <TableCell>
                      <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
                        {item.validFrom ? fmt(item.validFrom) : '—'}
                        {item.validTo ? ` → ${fmt(item.validTo)}` : ''}
                      </Text>
                    </TableCell>
                  )}
                  {/* Termín */}
                  {!isMobile && (
                    <TableCell>
                      <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
                        {item.deadlineDate
                          ? fmt(item.deadlineDate)
                          : item.deadlineDays
                            ? `${item.deadlineDays} dní`
                            : '—'}
                      </Text>
                    </TableCell>
                  )}
                  {/* Přiřazení */}
                  {!isMobile && (
                    <TableCell>
                      {item.totalAssigned === 0 ? (
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>—</Text>
                      ) : (
                        <Text size={200}>
                          {item.totalCompleted} / {item.totalAssigned}
                          <span style={{ color: tokens.colorNeutralForeground3, marginLeft: 4 }}>
                            ({completePct} %)
                          </span>
                        </Text>
                      )}
                    </TableCell>
                  )}
                  {/* Stav */}
                  <TableCell>
                    <Badge color={item.isActive ? 'success' : 'subtle'} appearance="tint">
                      {item.isActive ? 'Aktivní' : 'Neaktivní'}
                    </Badge>
                  </TableCell>
                  {/* Akce */}
                  {canEdit && !isMobile && (
                    <TableCell
                      onClick={e => e.stopPropagation()}
                      style={{ textAlign: 'right' }}
                    >
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<Edit24Regular />}
                        title="Upravit"
                        onClick={() => navigate(`/documents/${item.id}/edit`)}
                      />
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
      </div>

    </div>
  )
}
